import { useEffect, useState } from 'react';
import matchesMedia from './matchesMedia';

export const POPUP_DELAY_MS = 20_000;
export const POPUP_SCROLL_RATIO = 0.5;

function scrolledPast(ratio: number): boolean {
  const scrollable = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return false;
  return window.scrollY / scrollable >= ratio;
}

/**
 * True once the visitor has spent 20 seconds, scrolled half the page, or (on
 * desktop) moved the mouse out through the top of the window, whichever comes
 * first. Fires once per mount and then removes every listener. Pass
 * `enabled = false` while the popup could not show anyway, so nothing runs.
 */
export default function usePopupTrigger(enabled: boolean): boolean {
  const [fired, setFired] = useState(false);

  useEffect(() => {
    if (!enabled || fired) return undefined;
    const fire = () => setFired(true);

    const timer = window.setTimeout(fire, POPUP_DELAY_MS);
    const onScroll = () => {
      if (scrolledPast(POPUP_SCROLL_RATIO)) fire();
    };
    const onMouseOut = (event: MouseEvent) => {
      if (!event.relatedTarget && event.clientY <= 0) fire();
    };
    // Exit intent only means something with a mouse.
    const exitIntent = matchesMedia('(pointer: fine)');

    window.addEventListener('scroll', onScroll, { passive: true });
    if (exitIntent) document.addEventListener('mouseout', onMouseOut);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      if (exitIntent) document.removeEventListener('mouseout', onMouseOut);
    };
  }, [enabled, fired]);

  return fired;
}
